import React from 'react';
import Spinner from './Spinner';
import './Button.css';

/** RN-parity variants. */
export type ButtonVariant = 'filled' | 'outlined' | 'text' | 'popup';

/** Legacy web variants — kept while non-SignIn screens migrate to RN parity. */
type LegacyVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
type LegacySize = 'small' | 'medium' | 'large';

const LEGACY_VARIANT_MAP: Record<LegacyVariant, ButtonVariant> = {
  primary: 'filled',
  secondary: 'outlined',
  outline: 'outlined',
  ghost: 'text',
  danger: 'filled',
};

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'title'> {
  /** RN-style title prop. Either this or `children` is required. */
  title?: string;
  /** Legacy children-based label. */
  children?: React.ReactNode;
  variant?: ButtonVariant | LegacyVariant;
  /** Legacy size prop — ignored in new variants but accepted for back-compat. */
  size?: LegacySize;
  loading?: boolean;
  titleStyle?: React.CSSProperties;
  className?: string;
  style?: React.CSSProperties;
}

const isLegacy = (v: ButtonVariant | LegacyVariant): v is LegacyVariant =>
  v === 'primary' ||
  v === 'secondary' ||
  v === 'outline' ||
  v === 'ghost' ||
  v === 'danger';

/**
 * Web equivalent of `apps/WunderGuide/common/Button.tsx`.
 * Variants mirror RN — filled / outlined / text / popup.
 * Press animation uses CSS scale transform on :active.
 *
 * Accepts legacy variant names (primary/secondary/outline/ghost/danger) and
 * `children` for back-compat while non-SignIn screens migrate to RN parity.
 */
const Button: React.FC<ButtonProps> = ({
  title,
  children,
  variant = 'filled',
  loading = false,
  disabled = false,
  titleStyle,
  className = '',
  style,
  onClick,
  ...rest
}) => {
  const isDisabled = disabled || loading;
  const resolvedVariant: ButtonVariant = isLegacy(variant)
    ? LEGACY_VARIANT_MAP[variant]
    : variant;
  const isDanger = variant === 'danger';
  const variantClass = `wg-btn--${resolvedVariant}`;
  const dangerClass = isDanger ? 'wg-btn--danger' : '';
  const combined = `wg-btn ${variantClass} ${dangerClass} ${
    isDisabled ? 'wg-btn--disabled' : ''
  } ${className}`.trim();

  const label = title ?? children;

  return (
    <button
      type="button"
      className={combined}
      style={style}
      disabled={isDisabled}
      onClick={isDisabled ? undefined : onClick}
      {...rest}
    >
      {loading ? (
        <Spinner size="small" />
      ) : (
        <span className="wg-btn__label" style={titleStyle}>
          {label}
        </span>
      )}
    </button>
  );
};

export default Button;
